/**
 * Authentication Services - Hosting Application
 *
 * This module contains the real backend integration for authentication in the hosting app.
 * It handles:
 * - CSRF token management (double-submit cookie pattern)
 * - Session storage and persistence
 * - Agreement acceptance flow for onboarding
 * - Password reset functionality
 * - Global configuration fetching (e.g., CAPTCHA settings)
 *
 * Note: The @eruvaka/web-pondlogs-auth package provides UI components and reference
 * implementations. This file contains the production-grade API integration with
 * backend-specific logic (CSRF, session management, error handling).
 */
import { endpoint, env, ForbiddenResponseError, NotFoundResponseError, UnknownResponseError, } from '@eruvaka/web-pondlogs-base';
export class AuthRequestError extends Error {
    code;
    status;
    signinFailedCount;
    retryAfter;
    constructor(info) {
        super(info.message);
        this.name = 'AuthRequestError';
        this.code = info.code;
        this.status = info.status;
        this.signinFailedCount = info.signinFailedCount;
        this.retryAfter = info.retryAfter;
    }
}
const authEndpoint = (path) => {
    return endpoint(env.apiBaseUrl, path);
};
// CSRF \u2014 disabled
// const isLocalFrontendWithRemoteApi = (): boolean => {
//   if (typeof window === 'undefined') return false
//   const localHosts = ['localhost', '127.0.0.1', '[::1]']
//   if (!localHosts.includes(window.location.hostname)) return false
//   try {
//     const apiHost = new URL(env.apiBaseUrl).hostname
//     return !localHosts.includes(apiHost)
//   } catch {
//     return false
//   }
// }
const parseErrorMessage = (code, fallback) => {
    switch (code) {
        case 'INACTIVE_USER':
            return 'Your account is inactive.';
        case 'ACCOUNT_IS_NOT_EXISTS':
            return 'Account does not exist.';
        case 'PASSWORD_NOT_EXISTS':
            return 'Password is not set for this account.';
        case 'BLOCKED_DUE_TO_FAILED_ATTEMPTS':
        case 'ACCOUNT_BLOCKED':
            return 'Account is blocked due to multiple failed attempts.';
        case 'CAPTCHA_VERIFICATION_FAILED':
            return 'Captcha verification failed. Please try again.';
        case 'MISSING_CAPTCHA_TOKEN':
            return 'Captcha is required.';
        case 'PASSWORD_EXPIRED':
            return 'Your password has expired. Please reset it.';
        default:
            return fallback;
    }
};
const parseJson = async (response) => {
    try {
        return (await response.json());
    }
    catch {
        return {};
    }
};
// CSRF — disabled
// const extractCsrfToken = (payload: Record<string, unknown>): string | null => {
//   const direct = payload.token ?? payload.csrf_token ?? payload.csrfToken
//   if (typeof direct === 'string' && direct) {
//     return direct
//   }
//   const data = payload.data
//   if (data && typeof data === 'object') {
//     const nested = data as Record<string, unknown>
//     const nestedToken = nested.token ?? nested.csrf_token ?? nested.csrfToken
//     if (typeof nestedToken === 'string' && nestedToken) {
//       return nestedToken
//     }
//   }
//   return null
// }
// /**
//  * Fetches a CSRF token from the backend.
//  */
// export const fetchCsrfToken = async (): Promise<string> => {
//   const response = await fetch(authEndpoint(env.csrfTokenPath), {
//     method: 'GET',
//     credentials: 'include',
//     cache: 'no-store',
//     headers: { 'Content-Type': 'application/json' },
//   })
//   const payload = await parseJson(response)
//   if (!response.ok) {
//     const body = await response.text()
//     if (response.status === 403) throw new ForbiddenResponseError(body)
//     if (response.status === 404) throw new NotFoundResponseError(body)
//     throw new UnknownResponseError(response.status, body)
//   }
//   const token = extractCsrfToken(payload)
//   if (!token) throw new Error('CSRF token missing in response')
//   return token
// }
const postSignIn = async (credentials) => {
    const response = await fetch(authEndpoint(env.authSignInPath), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            username: credentials.loginId.trim(),
            password: credentials.password,
            // captchaToken: credentials.captchaToken ?? undefined, // Cloudflare — disabled
        }),
    });
    const payload = await parseJson(response);
    return { response, payload };
};
/**
 * Authenticates against backend API.
 */
export const signIn = async (credentials) => {
    // CSRF — disabled
    // const skipCsrf = isLocalFrontendWithRemoteApi()
    // let csrfToken: string | undefined
    // if (!skipCsrf) {
    //   try { csrfToken = await fetchCsrfToken() }
    //   catch (err) { console.warn('[CSRF] Failed to fetch CSRF token:', err) }
    // }
    let { response, payload } = await postSignIn(credentials);
    // BFF sends camelCase `errorCode`; legacy sends snake_case `error_code` — check both
    let errorCode = typeof payload.error_code === 'string'
        ? payload.error_code
        : typeof payload.errorCode === 'string'
            ? payload.errorCode
            : undefined;
    // const requireTokenRefresh = Boolean(payload.require_token_refresh)
    // CSRF retry — disabled
    // if (!skipCsrf && !response.ok && response.status === 403 && errorCode === 'EBADCSRFTOKEN' && requireTokenRefresh) {
    //   try {
    //     const refreshedToken = await fetchCsrfToken()
    //     const retryResult = await postSignIn(credentials, refreshedToken)
    //     response = retryResult.response
    //     payload = retryResult.payload
    //     errorCode = typeof payload.error_code === 'string' ? payload.error_code
    //       : typeof payload.errorCode === 'string' ? payload.errorCode : undefined
    //   } catch (err) { console.warn('[CSRF] Token refresh retry failed:', err) }
    // }
    if (!response.ok) {
        // Use the BFF's message directly when no structured error code is present
        const serverMessage = typeof payload.message === 'string' ? payload.message : undefined;
        const fallback = response.status === 401
            ? (serverMessage ?? 'Invalid credentials.')
            : 'Unable to sign in. Please try again.';
        throw new AuthRequestError({
            message: parseErrorMessage(errorCode, fallback),
            code: errorCode,
            status: response.status,
            signinFailedCount: typeof payload.signin_failed_count === 'number' ? payload.signin_failed_count : undefined,
            retryAfter: typeof payload.retry_after === 'number' ? payload.retry_after : undefined,
        });
    }
    const agreementRequired = Boolean(payload.agreement_acceptance_required);
    const token = payload.token ?? payload.access_token;
    if (typeof token !== 'string' || !token) {
        throw new AuthRequestError({ message: 'Authentication token missing in sign-in response.' });
    }
    const userDetails = payload.user_details;
    if (!userDetails || typeof userDetails !== 'object') {
        throw new AuthRequestError({ message: 'User details missing in sign-in response.' });
    }
    return {
        session: {
            user: userDetails,
            token,
            refreshToken: typeof payload.refresh_token === 'string' ? payload.refresh_token : undefined,
            passwordExpiring: Boolean(payload.password_expiring),
            daysUntilExpiry: typeof payload.days_until_expiry === 'number' ? payload.days_until_expiry : undefined,
        },
        // Carry agreement flag so UI can prompt acceptance later
        ...(agreementRequired && {
            agreementRequired: true,
            agreementContext: payload,
        }),
    };
};
export const fetchGlobalConfig = async () => {
    const response = await fetch(authEndpoint(env.globalConfigPath), {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const payload = await parseJson(response);
    if (!response.ok) {
        const body = await response.text();
        if (response.status === 403) {
            throw new ForbiddenResponseError(body);
        }
        if (response.status === 404) {
            throw new NotFoundResponseError(body);
        }
        throw new UnknownResponseError(response.status, body);
    }
    const data = (payload.data ?? payload);
    return {
        CF_CAPTCHA_ENABLE: Boolean(data.CF_CAPTCHA_ENABLE),
        SIGN_UP_ENABLED: Boolean(data.SIGN_UP_ENABLED),
    };
};
export const acceptAgreement = async (token, agreementContext) => {
    const response = await fetch(authEndpoint(env.agreementAcceptPath), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            agreement_context: agreementContext ?? {},
        }),
    });
    if (!response.ok) {
        const payload = await parseJson(response);
        throw new AuthRequestError({
            message: parseErrorMessage(typeof payload.error_code === 'string' ? payload.error_code : undefined, 'Unable to accept agreement. Please try again.'),
            code: typeof payload.error_code === 'string' ? payload.error_code : undefined,
            status: response.status,
        });
    }
};
/** Persists session to localStorage. */
export const storeSession = (session) => {
    localStorage.setItem(env.sessionStorageKey, JSON.stringify(session));
};
/** Retrieves the persisted session, or null if none exists. */
export const getStoredSession = () => {
    try {
        const raw = localStorage.getItem(env.sessionStorageKey);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.token !== 'string' || !parsed.token.trim())
            return null;
        if (!parsed.user || typeof parsed.user !== 'object')
            return null;
        const userRaw = parsed.user;
        if (typeof userRaw._id !== 'string' || !userRaw._id.trim())
            return null;
        if (typeof userRaw.email !== 'string' || !userRaw.email.trim())
            return null;
        if (typeof userRaw.full_name !== 'string' || !userRaw.full_name.trim())
            return null;
        return {
            token: parsed.token,
            refreshToken: parsed.refreshToken,
            user: userRaw,
            passwordExpiring: parsed.passwordExpiring,
            daysUntilExpiry: parsed.daysUntilExpiry,
        };
    }
    catch {
        return null;
    }
};
/** Removes the persisted session from localStorage. */
export const clearSession = () => {
    localStorage.removeItem(env.sessionStorageKey);
};
/**
 * Sends a forgot-password email to the address associated with the loginId.
 */
export const forgotPassword = async (loginId) => {
    const response = await fetch(authEndpoint(env.forgotPasswordPath), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_id: loginId.trim() }),
    });
    if (!response.ok) {
        const payload = await parseJson(response);
        const errorCode = typeof payload.error_code === 'string' ? payload.error_code : undefined;
        const fallbackMap = {
            EMAIL_NOT_VERIFIED: 'Your email is not verified. Please check your inbox for the verification link.',
            MOBILE_NOT_VERIFIED: 'Your mobile is not verified. Please verify before resetting.',
            ACCOUNT_IS_NOT_EXISTS: 'No account found with that username or email.',
            INACTIVE_USER: 'Your account is inactive. Please contact support.',
        };
        throw new AuthRequestError({
            message: errorCode && fallbackMap[errorCode]
                ? fallbackMap[errorCode]
                : 'Unable to send reset email. Please try again.',
            code: errorCode,
            status: response.status,
        });
    }
};
/**
 * Notifies the backend of a sign-out (best-effort; does not throw).
 */
export const signOutRemote = async (token) => {
    try {
        // Legacy Vue sends raw access token in Authorization header (no Bearer prefix).
        // Keep that behavior first, then retry with Bearer for compatibility.
        let response = await fetch(authEndpoint(env.authSignOutPath), {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
        });
        if (response.status === 401) {
            response = await fetch(authEndpoint(env.authSignOutPath), {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
        }
    }
    catch {
        // best-effort — local session is always cleared regardless
    }
};
